import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core'
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiLabel, TuiTextfield } from '@taiga-ui/core'
import { TuiChevron, TuiDataListWrapper, TuiSelect } from '@taiga-ui/kit'
import { Subject, takeUntil } from 'rxjs'

@Component({
	selector: 'app-select',
	templateUrl: './select.component.html',
	styleUrl: './select.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		FormsModule,
		TranslatePipe,
		TuiLabel,
		TuiTextfield,
		TuiChevron,
		TuiDataListWrapper,
		TuiSelect,
	],
})
export class SelectComponent<T = string> implements OnInit, OnDestroy {
	protected readonly string: StringConstructor = String

	private _destroy$: Subject<void> = new Subject<void>()

	public control: InputSignal<AbstractControl | null> = input<AbstractControl | null>(null)
	public value: InputSignal<T | null> = input<T | null>(null)
	public items: InputSignal<T[]> = input<T[]>([])
	public translationPrefix: InputSignal<string> = input<string>('')
	public valueChange: OutputEmitterRef<T> = output<T>()

	private _controlValue: WritableSignal<T | null> = signal<T | null>(null)

	protected formControl: Signal<FormControl> = computed<FormControl>((): FormControl => {
		return (this.control() as FormControl) ?? new FormControl()
	})

	protected currentValue: Signal<T | null> = computed<T | null>((): T | null => {
		return this.control() ? this._controlValue() : this.value()
	})

	protected getLabel(item: T | null): string {
		if (!item) return ''
		if (item instanceof Object && 'title' in item) return this.string(item.title)
		return this.translationPrefix() + '.' + item.toString()
	}

	private _listenFormControlChanges(): void {
		const control: AbstractControl | null = this.control()
		if (!control) return

		this._controlValue.set(control.value)
		control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((val: T): void => this._controlValue.set(val))
	}

	ngOnInit(): void {
		this._listenFormControlChanges()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
